// ─────────────────────────────────────────────────────────────────────────────
// power.js — Oarlock Power (OPW).
//
// OPW (Watts) = (work per stroke, J) × (strokes per second)
//             = trapezoid_integral(force, angle_in_radians) × (SPM / 60)
//
// Inputs are paired arrays of force (N) and angle (deg). Force/angle MUST be
// zero-corrected. The trapezoid integral is computed only across the drive
// phase (force > catchThreshold .. last sample where force > finishThreshold).
// ─────────────────────────────────────────────────────────────────────────────

import {
  catchAngle,
  finishAngle,
  DEFAULT_CATCH_THRESHOLD_N,
  DEFAULT_FINISH_THRESHOLD_N,
} from './catchFinish';

const DEG_TO_RAD = Math.PI / 180;

/**
 * Trapezoid integral of force over angle (radians). Returns work per stroke (J).
 * Only samples within [CA, FA] (inclusive) are included.
 */
export function workPerStrokeJ(forceN, angleDeg, opts = {}) {
  if (!Array.isArray(forceN) || !Array.isArray(angleDeg) || forceN.length !== angleDeg.length || forceN.length < 2) {
    return null;
  }
  const ca = catchAngle(forceN, angleDeg, opts.catchThreshold ?? DEFAULT_CATCH_THRESHOLD_N);
  const fa = finishAngle(forceN, angleDeg, opts.finishThreshold ?? DEFAULT_FINISH_THRESHOLD_N);
  if (ca == null || fa == null) return null;

  // Build the drive sub-arrays in original order.
  const fDrive = [];
  const aDrive = [];
  for (let i = 0; i < forceN.length; i += 1) {
    if (angleDeg[i] >= ca && angleDeg[i] <= fa) {
      fDrive.push(forceN[i]);
      aDrive.push(angleDeg[i] * DEG_TO_RAD);
    }
  }
  if (fDrive.length < 2) return null;

  let work = 0;
  for (let i = 0; i < fDrive.length - 1; i += 1) {
    const dTheta = aDrive[i + 1] - aDrive[i];
    const fAvg = (fDrive[i] + fDrive[i + 1]) / 2;
    work += fAvg * dTheta;
  }
  return work; // Joules per stroke
}

/**
 * Oarlock Power (OPW) in Watts.
 * @param {{ forceN, angleDeg, spm }} args — paired arrays + strokes-per-minute.
 */
export function oarlockPower({ forceN, angleDeg, spm } = {}) {
  if (typeof spm !== 'number' || spm <= 0) return null;
  const work = workPerStrokeJ(forceN, angleDeg);
  if (work == null) return null;
  return work * (spm / 60);
}
